import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { timeAgo } from '@/lib/utils';
import { Seo } from '@/components/Seo';
import { FullPageLoader } from '@/components/Loaders';

export default function StorySharePage() {
  const { storyId } = useParams();
  const [story, setStory] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/stories/${storyId}`)
      .then((r) => setStory(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [storyId]);

  if (loading) return <FullPageLoader />;

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo title="Story | Thirrsty.com" path={`/story/${storyId}`} noindex />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-5 py-8">
        <div className="mb-5 flex items-center justify-center"><Logo /></div>
        {error || !story ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="story-share-expired">
            <p className="font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>This story has expired</p>
            <p className="mt-2 text-sm text-white/60">Stories on Thirrsty disappear after 24 hours. Join to catch the next one live.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="story-share-card">
            {story.video_url ? (
              <video src={story.video_url} controls autoPlay muted playsInline className="max-h-[60vh] w-full bg-black object-contain" data-testid="story-share-video" />
            ) : (
              <img src={story.image_data_url} alt="story" className="max-h-[60vh] w-full object-cover" data-testid="story-share-image" />
            )}
            <div className="flex items-center gap-2 p-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={story.user_avatar_url} />
                <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A] text-xs">{(story.user_display_name || 'U').slice(0, 1)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-semibold text-white">{story.user_display_name}</span>
              <span className="text-[11px] text-white/55">{timeAgo(story.created_at)} ago</span>
            </div>
            {story.caption && <p className="px-3 pb-3 text-sm text-white/90">{story.caption}</p>}
          </div>
        )}
        <div className="mt-6 rounded-2xl border border-[#F6B24A]/25 bg-[#F6B24A]/8 p-4 text-center">
          <p className="font-display text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Nightlife lives on Thirrsty</p>
          <p className="mt-1 text-xs text-white/65">Post stories, go live at venues, and unlock drink discounts with your personal QR.</p>
          <Button asChild className="mt-3 h-11 w-full rounded-xl bg-[#F6B24A] font-semibold text-[#1A1208] hover:bg-[#FFC065]" data-testid="story-share-join-button">
            <Link to="/auth/signup">Join Thirrsty — it's free</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
